import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import styles from '../styles/components/PanelTiles.module.css';
import plusIcon from '../assets/plus_white.svg';
import arrowLeftIcon from '../assets/arrow_left_blue.svg';
import Modal from '@ax2/common/components/Modal';

export interface PanelTileInfo {
  title: JSX.Element;
  imageSrc: string;
  imageAlt: string;
  description: JSX.Element;
  links: {
    linkTitle: string;
    modalTitle: string;
    modalContent: JSX.Element;
  }[];
}

interface TileProps {
  tileInfo: PanelTileInfo;
  isExpanded: boolean;
  widthClass: string;
  toggle: () => void;
}

interface ModalDetails {
  title: string;
  content: JSX.Element;
}

const Tile: React.FC<TileProps> = ({
  tileInfo,
  isExpanded,
  widthClass,
  toggle,
}) => {
  // This delay must be equal to the corresponding delay in the CSS module file
  const delay = 200;
  const [imageVisible, setImageVisible] = useState(true);
  const [modalDetails, setModalDetails] = useState<ModalDetails>();
  useEffect(() => {
    if (!isExpanded) {
      setTimeout(() => {
        setImageVisible(true);
      }, delay);
    } else {
      setImageVisible(false);
    }
  }, [delay, isExpanded]);
  const description = imageVisible ? null : (
    <div className={styles.description}>{tileInfo.description}</div>
  );
  const image = imageVisible ? (
    <img
      className={styles.image}
      src={tileInfo.imageSrc}
      aria-hidden="true"
      alt={tileInfo.imageAlt}
    />
  ) : null;

  return (
    <div className={[styles.tile, widthClass].join(' ')}>
      {modalDetails && (
        <Modal onClose={() => setModalDetails(undefined)}>
          <div className={styles.modalDetails}>
            <div className={styles.modalTitle}>{modalDetails.title}</div>
            {modalDetails.content}
          </div>
        </Modal>
      )}
      <div
        className={[
          styles.tileContent,
          isExpanded ? styles.tileContentExpanded : '',
        ].join(' ')}
      >
        <h4
          className={[
            styles.title,
            imageVisible ? '' : styles.smallerOffset,
          ].join(' ')}
        >
          {tileInfo.title}
        </h4>
        <div
          className={
            isExpanded ? styles.descriptionWrapper : styles.imageWrapper
          }
        >
          {description}
          {image}
        </div>
        <div className={styles.expandButtonContainer}>
          <div
            className={styles.button}
            onClick={toggle}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <img src={arrowLeftIcon} alt="Collapse description button" />
            ) : (
              <img src={plusIcon} alt="Expand description button" />
            )}
          </div>
        </div>
        <div className={styles.links}>
          {tileInfo.links.map((l, idx) => (
            <button
              key={idx}
              className={styles.link}
              onClick={() => {
                setModalDetails({
                  title: l.modalTitle,
                  content: l.modalContent,
                });
              }}
            >
              {l.linkTitle}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export interface PanelTilesProps {
  tiles: PanelTileInfo[];
}

export const PanelTiles: React.FC<PanelTilesProps> = ({ tiles }) => {
  // IMPORTANT: When changing anything, make sure to adjust these values in the corresponding module.css file as well.
  const expectedMaxW = 1150;
  const nTiles = 6;
  const collapsedMaxW = expectedMaxW / nTiles; // including inner margin/padding
  const collapsedMinW = expectedMaxW / (nTiles + 1);
  const expandedW = collapsedMinW * 2;

  const tileDistribution: {
    [idx: string]: number[][];
  } = {
    '1': [[0, 1, 2, 3, 4, 5]],
    '2': [[0, 1, 2], [3, 4, 5]],
    '3': [[0, 1], [2, 3], [4, 5]],
    '6': [[0], [1], [2], [3], [4], [5]],
  };

  const [nRows, setNRows] = useState(1);
  const [expandedTileIdx, setExpandedTileIdx] = useState<number | null>(null);
  const tilesContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function calcMinWidthLimit(nTiles: number): number {
      return Math.max(
        collapsedMaxW * nTiles,
        collapsedMinW * (nTiles - 1) + expandedW
      );
    }

    const listener = () => {
      if (tilesContainer.current) {
        const width = tilesContainer.current.clientWidth;
        if (width >= calcMinWidthLimit(5)) {
          setNRows(2);
        } else if (width >= calcMinWidthLimit(3)) {
          setNRows(2);
        } else if (width >= calcMinWidthLimit(2)) {
          setNRows(3);
        } else {
          setNRows(6);
        }
      }
    };

    listener();
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, [tilesContainer, collapsedMaxW, collapsedMinW, expandedW]);

  if (tiles.length !== nTiles) {
    throw new Error(
      `PanelTiles component can only display ${nTiles} tiles, but ${tiles.length} were given`
    );
  }

  function getTilesDistribution() {
    return tileDistribution[nRows.toString()];
  }

  function isExpanded(idx: number): boolean {
    return idx === expandedTileIdx;
  }

  function hasExpandedNeighbor(idx: number): boolean {
    if (expandedTileIdx !== null && expandedTileIdx !== idx) {
      const distr = getTilesDistribution();
      if (distr) {
        return _.some(
          distr,
          row => _.includes(row, idx) && _.includes(row, expandedTileIdx)
        );
      }
    }
    return false;
  }

  function calcWidthClass(idx: number): string {
    const oneColumn = nRows === nTiles;
    if (isExpanded(idx)) {
      return oneColumn ? styles.tileExpandedOneColumn : styles.tileExpanded;
    } else if (hasExpandedNeighbor(idx)) {
      return oneColumn
        ? styles.tileCollapsedOneColumn
        : styles.tileCollapsedMin;
    }
    return oneColumn ? styles.tileCollapsedOneColumn : styles.tileCollapsedMax;
  }

  return (
    <div ref={tilesContainer} className={styles.tilesContainer}>
      {_.range(0, nRows).map(rowIdx => (
        <div key={rowIdx} className={styles.tilesRow}>
          {tiles
            .map((t, idx) => [idx, t] as [number, PanelTileInfo])
            .filter(([idx, _t]) =>
              _.includes(getTilesDistribution()[rowIdx], idx)
            )
            .map(([idx, t]) => (
              <Tile
                key={idx}
                tileInfo={t}
                isExpanded={isExpanded(idx)}
                widthClass={calcWidthClass(idx)}
                toggle={() => setExpandedTileIdx(isExpanded(idx) ? null : idx)}
              />
            ))}
        </div>
      ))}
    </div>
  );
};
